import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bookmark, CheckCircle2, ChevronDown, Eye, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { tearTicket, rollCredits } from "@/lib/cinemaFx";
import { requestTicket } from "@/lib/guest";


export type WatchStatus = "saved" | "watching" | "watched";

type WatchStatusValue = WatchStatus | null;

interface WatchStatusSelectorProps {
  tmdbId: number;
  mediaType: "movie" | "tv";
  userId?: string;
  title?: string;
  posterPath?: string | null;
  releaseDate?: string | number | null;
  currentStatus?: WatchStatusValue;
  onStatusChange?: (newStatus: WatchStatusValue) => void;
  compact?: boolean;
  display?: "dropdown" | "segmented";
  allowRemove?: boolean;
}

const STATUS_CONFIG: Record<
  WatchStatus,
  {
    label: string;
    actionLabel: string;
    icon: typeof Bookmark;
    activeClassName: string;
  }
> = {
  saved: {
    label: "Saved",
    actionLabel: "Save",
    icon: Bookmark,
    activeClassName: "border-info/30 bg-info/10 text-info hover:bg-info/15",
  },
  watching: {
    label: "Watching",
    actionLabel: "Watching",
    icon: Eye,
    activeClassName: "border-warning/30 bg-warning/10 text-warning hover:bg-warning/15",
  },
  watched: {
    label: "Watched",
    actionLabel: "Watched",
    icon: CheckCircle2,
    activeClassName: "border-success/30 bg-success/10 text-success hover:bg-success/15",
  },
};

export const WatchStatusSelector = ({
  tmdbId,
  mediaType,
  userId,
  title,
  posterPath,
  releaseDate,
  currentStatus,
  onStatusChange,
  compact = false,
  display = "dropdown",
  allowRemove = true,
}: WatchStatusSelectorProps) => {
  const hasSuppliedStatus = currentStatus !== undefined;
  const [status, setStatus] = useState<WatchStatusValue>(currentStatus ?? null);
  const [updating, setUpdating] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setStatus(currentStatus ?? null);
  }, [currentStatus, tmdbId, mediaType]);

  useEffect(() => {
    let cancelled = false;

    const loadExactStatus = async () => {
      if (!userId) {
        if (!cancelled) setStatus(null);
        return;
      }
      if (hasSuppliedStatus) return;

      setLoadingStatus(true);
      try {
        const { data, error } = await supabase
          .from("user_favorites")
          .select("watch_status")
          .eq("user_id", userId)
          .eq("tmdb_id", tmdbId)
          .eq("media_type", mediaType)
          .maybeSingle();

        if (error && error.code !== "PGRST116") throw error;

        if (!cancelled) {
          setStatus(((data?.watch_status as WatchStatus | undefined) ?? null));
        }
      } catch (error) {
        console.error("Error loading watch status:", error);
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    };

    loadExactStatus();

    return () => {
      cancelled = true;
    };
  }, [userId, tmdbId, mediaType, hasSuppliedStatus]);

  const persistStatus = async (nextStatus: WatchStatusValue) => {
    if (!userId) {
      requestTicket("track what you watch");
      return;
    }

    if (nextStatus && !title) {
      toast({
        title: "Missing details",
        description: "Please open the title details and try again.",
        variant: "destructive",
      });
      return;
    }

    setUpdating(true);
    try {
      const { error: deleteError } = await supabase
        .from("user_favorites")
        .delete()
        .eq("user_id", userId)
        .eq("tmdb_id", tmdbId)
        .eq("media_type", mediaType);

      if (deleteError) throw deleteError;

      if (nextStatus) {
        const { error: insertError } = await supabase.from("user_favorites").insert({
          user_id: userId,
          tmdb_id: tmdbId,
          media_type: mediaType,
          title: title,
          poster_path: posterPath ?? null,
          release_date: releaseDate != null ? String(releaseDate) : null,
          watch_status: nextStatus,
        });

        if (insertError) throw insertError;
      }

      setStatus(nextStatus);
      onStatusChange?.(nextStatus);

      if (nextStatus) {
        toast({
          title: `Status: ${STATUS_CONFIG[nextStatus].label}`,
          description: `${title ?? "Item"} is now marked as ${STATUS_CONFIG[nextStatus].label.toLowerCase()}.`,
        });
      } else {
        toast({
          title: "Removed from watchlist",
          description: `${title ?? "Item"} is no longer tracked.`,
        });
      }
    } catch (error) {
      console.error("Error updating watch status:", error);
      toast({
        title: "Error",
        description: "Failed to update watch status.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (
    nextStatus: WatchStatus,
    e?: React.MouseEvent,
  ) => {
    const prev = status;
    // Capture coordinates before Radix closes/unmounts menu items; otherwise
    // the reward animation can originate from nowhere.
    const target = e?.currentTarget as HTMLElement | undefined;
    const rect = target?.getBoundingClientRect();
    const ox = rect ? rect.left + rect.width / 2 : undefined;
    const oy = rect ? rect.top + rect.height / 2 : undefined;

    if (nextStatus === status) {
      if (!allowRemove) return;
      await persistStatus(null);
      return;
    }

    await persistStatus(nextStatus);

    if (nextStatus === "saved" && prev !== "saved") {
      tearTicket({ title, posterPath, originX: ox, originY: oy });
    } else if (nextStatus === "watched" && prev !== "watched") {
      rollCredits({ title, originX: ox, originY: oy });
    }
  };


  const activeConfig = status ? STATUS_CONFIG[status] : null;
  const ActiveIcon = activeConfig?.icon ?? Bookmark;
  const isBusy = updating || loadingStatus;

  const [open, setOpen] = useState(false);

  // Accidental-tap safety: a press only counts as a deliberate click when the
  // pointer barely moved and wasn't held down (i.e. not a scroll or long press).
  const pressRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const MOVE_TOLERANCE = 10; // px
  const MAX_PRESS_MS = 700;

  const onGuardPointerDown = (event: React.PointerEvent) => {
    pressRef.current = { x: event.clientX, y: event.clientY, t: Date.now() };
  };

  const onGuardPointerMove = (event: React.PointerEvent) => {
    const start = pressRef.current;
    if (!start) return;
    if (
      Math.abs(event.clientX - start.x) > MOVE_TOLERANCE ||
      Math.abs(event.clientY - start.y) > MOVE_TOLERANCE
    ) {
      pressRef.current = null; // treat as a scroll/drag, not a tap
    }
  };

  const isDeliberateTap = (event?: React.MouseEvent) => {
    const start = pressRef.current;
    pressRef.current = null;
    // Keyboard activation (Enter/Space) reports detail === 0 — always allow it.
    if (event && event.detail === 0) return true;
    if (!start) return false;
    return Date.now() - start.t <= MAX_PRESS_MS;
  };

  const dropdown = (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isBusy}
          onPointerDown={(event) => {
            // Never open on pointer-down — that is what hijacks mobile scrolling.
            event.preventDefault();
            event.stopPropagation();
            onGuardPointerDown(event);
          }}
          onPointerMove={onGuardPointerMove}
          onPointerCancel={() => { pressRef.current = null; }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isDeliberateTap(event)) return;
            // Guests see the same button; using it opens the seat prompt.
            if (!userId) {
              requestTicket("track what you watch");
              return;
            }
            setOpen((prev) => !prev);
          }}
          className={cn(
            "h-8 gap-1 text-xs border-border/50 bg-background/60 hover:bg-muted/50 touch-manipulation",
            activeConfig?.activeClassName
          )}
        >
          <ActiveIcon className="h-3.5 w-3.5" />
          {!compact && <span>{activeConfig?.label ?? "Track"}</span>}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {(Object.entries(STATUS_CONFIG) as [WatchStatus, (typeof STATUS_CONFIG)[WatchStatus]][]).map(([key, cfg]) => {
          const ItemIcon = cfg.icon;
          const isActive = key === status;

          return (
            <DropdownMenuItem
              key={key}
              onClick={(e) => handleStatusChange(key, e as unknown as React.MouseEvent)}

              className={cn(isActive && "bg-muted font-medium")}
            >
              <ItemIcon className={cn("mr-2 h-4 w-4", isActive && cfg.activeClassName.split(" ")[2])} />
              {cfg.label}
              {isActive && <span className="ml-auto text-xs text-muted-foreground">Current</span>}
            </DropdownMenuItem>
          );
        })}
        {allowRemove && status && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => persistStatus(null)}>
              <X className="mr-2 h-4 w-4" />
              Remove
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (display === "segmented") {
    return (
      <>
        <div className="hidden sm:grid grid-cols-3 gap-1.5">
          {(Object.entries(STATUS_CONFIG) as [WatchStatus, (typeof STATUS_CONFIG)[WatchStatus]][]).map(([key, cfg]) => {
            const ItemIcon = cfg.icon;
            const isActive = key === status;

            return (
              <Button
                key={key}
                type="button"
                variant="outline"
                size="sm"
                disabled={isBusy}
                onPointerDown={onGuardPointerDown}
                onPointerMove={onGuardPointerMove}
                onPointerCancel={() => { pressRef.current = null; }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDeliberateTap(e)) return;
                  void handleStatusChange(key, e);
                }}
                title={allowRemove && isActive ? `Click again to remove ${title ?? "this title"}` : cfg.label}
                className={cn(
                  "h-8 px-2 text-[11px] border-border/50 bg-background/60 text-muted-foreground hover:bg-muted/50 touch-manipulation",
                  isActive && cfg.activeClassName
                )}
              >
                <ItemIcon className="mr-1 h-3.5 w-3.5" />
                <span className="truncate">{isActive ? cfg.label : cfg.actionLabel}</span>
              </Button>
            );
          })}
        </div>
        <div className="sm:hidden">{dropdown}</div>
      </>
    );
  }

  return dropdown;
};